import type { ReactNode } from 'react';
import { cx } from '../cx';

export type DistributionBin = {
  id: string;
  from: number;
  to: number;
  count: number;
  /** Range label ("3,000 to 3,500"). */ label: ReactNode;
};

export type DistributionBarsProps = {
  bins: DistributionBin[];
  /** The middle half of the distribution; bins inside it are highlighted, the rest are context. */
  middle: { p25: number; p75: number; label: ReactNode };
  title: ReactNode;
  /** Sample and period, required ("214 listings with monthly gross pay, last 90 days"). */
  source: ReactNode;
  tableHeaders: { range: ReactNode; count: ReactNode };
  className?: string;
};

/** A bin is in the middle half when its midpoint falls between p25 and p75. Pure, exported for tests. */
export const inMiddleHalf = (bin: Pick<DistributionBin, 'from' | 'to'>, p25: number, p75: number): boolean => {
  const mid = (bin.from + bin.to) / 2;
  return mid >= p25 && mid <= p75;
};

/** How pay is spread: one bar per pay band, the middle half in sun and the rest as context. */
export function DistributionBars({ bins, middle, title, source, tableHeaders, className }: DistributionBarsProps) {
  const max = Math.max(1, ...bins.map((b) => b.count));
  return (
    <figure className={cx('m-0 flex flex-col gap-3', className)}>
      <figcaption className="type-subheading text-jc-ink">{title}</figcaption>
      <div aria-hidden="true" className="flex h-36 items-end gap-1 border-b border-jc-border">
        {bins.map((b) => (
          <span
            key={b.id}
            className={cx(
              'flex-1 rounded-t-jc-info',
              inMiddleHalf(b, middle.p25, middle.p75) ? 'bg-jc-cta' : 'bg-jc-chart-context',
            )}
            style={{ height: `${(b.count / max) * 100}%` }}
          />
        ))}
      </div>
      <div aria-hidden="true" className="flex justify-between text-xs tabular-nums text-jc-ink-secondary">
        <span>{bins[0]?.label}</span>
        <span>{bins[bins.length - 1]?.label}</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-jc-ink-secondary">
        <span className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="size-2.5 rounded-jc-info bg-jc-cta" />
          {middle.label}
        </span>
        <span>{source}</span>
      </div>
      <table className="sr-only">
        <thead>
          <tr>
            <th scope="col">{tableHeaders.range}</th>
            <th scope="col">{tableHeaders.count}</th>
          </tr>
        </thead>
        <tbody>
          {bins.map((b) => (
            <tr key={b.id}>
              <th scope="row">{b.label}</th>
              <td>{b.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
